/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text } from 'react-native';
import { Colors } from '../../themes';

export type AlertsManagementTableData = {
  id: string;
  name: string;
  routineName: string;
  assetName: string;
  groupName: string;
  productName: string;
  lastAlertDate: string;
  nextAlertDate: string;
  createdAt: string;
  disabled: boolean;
};

const getLabel = (labelStyle: any, label: string | null) => {
  return (
    <Text style={[labelStyle, styles.cellText]}>{label || '-'}</Text>
  );
};

const getInfoLabel = (labelStyle: any, info: CellContext<AlertsManagementTableData, unknown>) => {
  return getLabel(labelStyle, info.getValue() as string);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const BaseColumns: Record<
  string,
  {
    element: (info: CellContext<AlertsManagementTableData, unknown>) => React.ReactElement;
    exportCell?: (info: AlertsManagementTableData) => string;
  }
> = {
  name: {
    element: (info) => getInfoLabel(styles.nameLabel, info),
    exportCell: (data) => data.name || '',
  },
  routineName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.routineName || '',
  },
  assetName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.assetName || '',
  },
  groupName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.groupName || '',
  },
  productName: {
    element: (info) => getInfoLabel(styles.cellLabel, info),
    exportCell: (data) => data.productName || '',
  },
  lastAlertDate: {
    element: (info) => getLabel(styles.dateLabel, formatDate(info.getValue() as string)),
    exportCell: (data) => formatDate(data.lastAlertDate),
  },
  nextAlertDate: {
    element: (info) => getLabel(styles.dateLabel, formatDate(info.getValue() as string)),
    exportCell: (data) => formatDate(data.nextAlertDate),
  },
  createdAt: {
    element: (info) => getLabel(styles.dateLabel, formatDate(info.getValue() as string)),
    exportCell: (data) => formatDate(data.createdAt),
  },
};

export const useColumns = (
  filter: any
): ColumnDef<AlertsManagementTableData, any>[] => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<AlertsManagementTableData>();

  const columns = useMemo(() => {
    const cols: ColumnDef<AlertsManagementTableData, any>[] = [];

    if (!filter?.columns) return cols;

    filter.columns.forEach((column: string) => {
      const baseColumn = BaseColumns[column];
      if (!baseColumn) return;

      cols.push(
        columnHelper.accessor(column as any, {
          cell: baseColumn.element,
          header: () => t(`alertsManagement.list.${column}`),
          meta: {
            sortable: true,
            exportCell: baseColumn.exportCell,
          },
        })
      );
    });

    return cols;
  }, [filter, columnHelper, t]);

  return columns;
};

const styles = StyleSheet.create({
  cellText: {
    fontSize: 14,
    flexWrap: 'nowrap',
  },
  nameLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.blackText,
  },
  cellLabel: {
    fontSize: 14,
    color: Colors.blackText,
  },
  dateLabel: {
    fontSize: 14,
    color: Colors.blackText,
  },
});
